import { Controller } from '@hotwired/stimulus'
import { capitalCase } from 'change-case'
import { EventWithParams } from '../../types/common'

const ALERT_TONE_DATA: Record<
  string,
  {
    className: string
    icon: string
    role?: 'status' | 'alert'
  }
> = {
  alert: {
    className: 'alert-error',
    icon: 'icon-status-warning',
  },
  error: {
    className: 'alert-error',
    icon: 'icon-status-warning',
    role: 'alert',
  },
  info: {
    className: 'alert-info',
    icon: 'icon-status-info',
  },
  notice: {
    className: 'alert-info',
    icon: 'icon-status-success',
  },
  success: {
    className: 'alert-success',
    icon: 'icon-status-success',
  },
  warning: {
    className: 'alert-warning',
    icon: 'icon-status-warning',
  },
}

type DismissEvent = EventWithParams<{
  soft: boolean
}>

type AlertTone = keyof typeof ALERT_TONE_DATA

export default class AlertController extends Controller {
  declare toneValue: AlertTone
  declare contentValue: string
  declare readonly dismissibleValue: boolean
  declare readonly iconTarget: SVGSVGElement
  declare readonly textTarget: HTMLElement
  declare readonly dismissTarget: HTMLButtonElement
  declare readonly hasDismissTarget: boolean

  static values = {
    tone: String,
  }

  static targets = ['icon', 'text']

  dismiss(event: DismissEvent) {
    this.#toggleVisibility(false, event.params?.soft)
  }

  /***
   * Use this to programatically toggle visibility
   */
  softDismiss() {
    this.#toggleVisibility(false, true)
  }

  /***
   * This function is meant to provider other controller a way to show alerts at any moment
   * using an AlertController outlet
   */
  show(tone: AlertTone, content: string) {
    tone && this.setTone(tone)
    content && this.setContent(content)
    this.#toggleVisibility(true)
  }

  setContent(value: string) {
    this.textTarget.innerHTML = value
  }

  setTone(tone: AlertTone) {
    this.toneValue = tone
  }
  toneValueChanged(value: AlertTone, previousValue: AlertTone) {
    if (previousValue && previousValue !== value) {
      this.#swapIcon(value)

      this.element.role = ALERT_TONE_DATA[value].role || 'status'
      this.element.classList.remove(ALERT_TONE_DATA[previousValue].className)
      this.element.classList.add(ALERT_TONE_DATA[value].className)
    }
  }

  #swapIcon(tone: AlertTone) {
    const title = capitalCase(tone)
    const iconTitle = this.iconTarget.querySelector('title')
    const iconSource = this.iconTarget.querySelector('use')
    iconTitle.innerHTML = title
    iconSource.setAttribute('href', `#${ALERT_TONE_DATA[tone].icon}`)
  }

  #toggleVisibility(shown: boolean, softDismiss: boolean = false) {
    if (!shown) {
      // Hide (Leave transition)
      this.element.classList.add('scale-90')
      setTimeout(() => {
        if (softDismiss) {
          this.element.classList.add('tw-hidden', 'scale-100')
          this.element.classList.remove('scale-90')
        } else {
          this.element.remove()
        }
        this.dispatch('dismissed')
      }, 50) // Must match your duration-[50ms] class
    } else {
      this.element.classList.remove('tw-hidden')
      this.dispatch('shown')
    }
  }
}
